import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { requestNotificationPermission, onMessageListener, isSupported } from '../firebase';
import { toast } from 'sonner';
import axios from 'axios';

// Use current domain for API calls (works on both custom domains and preview)
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [fcmToken, setFcmToken] = useState(null);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    // Check if notifications are supported (basic check)
    const checkSupport = () => {
      try {
        const basicSupport = isSupported();
        setSupported(basicSupport);
      } catch (error) {
        console.log('Notification support check failed:', error);
        setSupported(false);
      }
    };
    
    checkSupport();
    
    // Check current permission status
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        setPermission(Notification.permission);
      } catch (error) {
        console.log('Could not check notification permission');
      }
    }
  }, []);

  useEffect(() => {
    // Listen for foreground messages - only if supported and permission granted
    if (!supported || permission !== 'granted') return;
    
    let cancelled = false;
    
    const setupListener = async () => {
      try {
        const payload = await onMessageListener();
        
        if (cancelled) return;
        
        console.log('Foreground notification:', payload);
        
        // Show toast notification
        toast.message(payload.notification?.title || 'SmartKick', {
          description: payload.notification?.body,
          action: payload.data?.matchId ? {
            label: 'View',
            onClick: () => window.location.href = `/app/match/${payload.data.matchId}`
          } : undefined
        });
      } catch (err) {
        if (!cancelled) {
          console.log('Message listener not available:', err);
        }
      }
    };
    
    setupListener();
    
    return () => {
      cancelled = true;
    };
  }, [supported, permission]);

  const requestPermission = useCallback(async () => {
    if (!supported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        setFcmToken(token);
        setPermission('granted');
        
        // Save token to backend
        try {
          await axios.post(`${API_URL}/notifications/register`, {
            fcm_token: token
          });
          toast.success('Notifications enabled! You\'ll receive alerts for your favorite teams.');
        } catch (error) {
          console.error('Failed to register token:', error);
        }
        
        return true;
      } else {
        setPermission('denied');
        toast.error('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Failed to enable notifications');
      return false;
    }
  }, [supported]);

  const value = {
    permission,
    fcmToken,
    supported,
    requestPermission,
    isEnabled: permission === 'granted'
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
